/**
 * Byte and encoding helpers for tailing a growing log file over WebDAV.
 *
 * Log deltas are fetched as raw bytes and can end in the middle of a character, so the
 * helpers here decide how many bytes are safe to decode now and which encoding to use.
 */

export type LogEncoding = "utf-8" | "utf-16le" | "utf-16be" | "windows-1252";

export function decodeBytes(bytes: Uint8Array, encoding: LogEncoding): string {
  try {
    return new TextDecoder(encoding).decode(bytes);
  } catch {
    return "";
  }
}

function isReadable(text: string): boolean {
  const nonPrintable = text.replace(/[\x09\x0A\x0D\x20-\x7E]/g, "");
  const ratio = nonPrintable.length / text.length;
  return ratio < 0.3; // Allow up to 30% non-printable characters
}

/**
 * Strictly valid UTF-8 without control characters (tab, line breaks and ESC for ANSI colours
 * aside). Catches non-Latin text like Cyrillic, which `isReadable` counts as non-printable.
 * UTF-16 text fails this through its NUL or other low bytes.
 */
function isCleanUtf8(bytes: Uint8Array): boolean {
  // A reload tail can start inside a character and a delta can end inside one. Bytes cut off
  // there prove nothing, so then the rest has to contain a non-ASCII character itself.
  let head = 0;
  while (head < 3 && head < bytes.length && (bytes[head] & 0xc0) === 0x80) head++;
  const partial = head > 0 || safeCharBoundary(bytes, "utf-8") < bytes.length;
  let text: string;
  try {
    // `stream` accepts a cut-off last character but still rejects bytes that can't start one.
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes.subarray(head), { stream: true });
  } catch {
    return false;
  }
  if (partial && !/[^\x00-\x7F]/.test(text)) return false;
  return !/[\x00-\x08\x0B\x0C\x0E-\x1A\x1C-\x1F\x7F]/.test(text);
}

/**
 * Picks the first encoding that yields mostly printable text and returns the decoded text
 * with it, so callers that need both don't decode twice.
 */
export function detectAndDecode(bytes: Uint8Array): { encoding: LogEncoding; text: string } {
  if (isCleanUtf8(bytes)) return { encoding: "utf-8", text: decodeBytes(bytes, "utf-8") };
  for (const encoding of ["utf-8", "utf-16le", "utf-16be"] as const) {
    const text = decodeBytes(bytes, encoding);
    if (isReadable(text)) return { encoding, text };
  }
  return { encoding: "windows-1252", text: decodeBytes(bytes, "windows-1252") };
}

export function detectEncoding(bytes: Uint8Array): LogEncoding {
  return detectAndDecode(bytes).encoding;
}

/** Byte length of the UTF-8 sequence a lead byte starts. */
function utf8SequenceLength(leadByte: number): number {
  if (leadByte >= 0xf0) return 4;
  if (leadByte >= 0xe0) return 3;
  if (leadByte >= 0xc0) return 2;
  return 1;
}

/**
 * Returns the largest length `<= bytes.length` that ends on a complete character, so the
 * remaining bytes can be re-fetched and decoded together with the rest of the character.
 */
export function safeCharBoundary(bytes: Uint8Array, encoding: LogEncoding): number {
  const length = bytes.length;

  if (encoding === "utf-8") {
    // Walk back over at most 3 continuation bytes (10xxxxxx) to the lead byte.
    for (let i = length - 1; i >= Math.max(0, length - 4); i--) {
      const byte = bytes[i];
      if ((byte & 0xc0) === 0x80) continue;
      return i + utf8SequenceLength(byte) > length ? i : length;
    }
    // No lead byte in reach: the data isn't valid UTF-8 here, don't stall on it.
    return length;
  }

  if (encoding === "utf-16le" || encoding === "utf-16be") {
    const even = length - (length % 2);
    if (even < 2) return even;
    const unit =
      encoding === "utf-16le" ? bytes[even - 2] | (bytes[even - 1] << 8) : (bytes[even - 2] << 8) | bytes[even - 1];
    // A trailing high surrogate needs the low surrogate of the next delta.
    const isHighSurrogate = unit >= 0xd800 && unit <= 0xdbff;
    return isHighSurrogate ? even - 2 : even;
  }

  return length;
}

/**
 * Builds a `Range` header for the bytes `[start, end)`.
 *
 * GPD's WebDAV server does NOT follow RFC 7233 here: it treats the range end as exclusive and
 * clamps it to the file length (`content_length = end - start`, see `gpd/webdav/response.py`).
 * Sending `end - 1` would silently drop the last byte of the file.
 */
export function tailRange(start: number, end: number): string {
  return `bytes=${start}-${end}`;
}

/**
 * Rotation check: the bytes re-read in front of the current offset must match the bytes that
 * were consumed last time. Otherwise the file was replaced by a different one.
 */
export function fingerprintMatches(overlap: Uint8Array, fingerprint: Uint8Array): boolean {
  if (overlap.length !== fingerprint.length) return false;
  for (let i = 0; i < overlap.length; i++) {
    if (overlap[i] !== fingerprint[i]) return false;
  }
  return true;
}
